import { List, ListItem, ListItemText, Menu, TextField, Divider } from '@mui/material';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { DRIVER_UNSET_LABEL } from 'src/constants/assignmentLabels';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';

/** 未割当センチネル。API 側で driver_id -1 は null 化される契約。 */
const UNASSIGNED_DRIVER_ID = -1;

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (driver: Pick<PlanningsDriverEntity, 'id' | 'name'>) => void;
  drivers: PlanningsDriverEntity[];
  /** 既に他 row で利用中の driver id 群 (= 選択肢から除外) */
  excludeDriverIds: ReadonlySet<number>;
  /** 現在この row が指している driver id (= 除外対象から外す) */
  currentDriverId: number | null;
  /**
   * 打ち込んだ名前で割り当てる。マスタに無い人を当日だけ乗せる運用のための口で、
   * API 側が同名を探して無ければ作る。未指定ならこの導線を出さない。
   */
  onCreate?: (name: string) => void;
};

const DriverSelectMenu: FC<Props> = memo(({
  anchorEl, onClose, onSelect, drivers, excludeDriverIds, currentDriverId, onCreate,
}) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);
  const [currentRow, setCurrentRow] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!anchorEl) {
      setInput('');
      setQuery('');
    }
  }, [anchorEl]);
  useEffect(() => { if (!composing) setQuery(input); }, [composing, input]);

  /**
   * 開いたら選択中の行まで送る。一覧は常に先頭から始まるので、ドライバーが多い会社では
   * いま乗っている人が画面外に居て見えなかった（指摘 2026/08/18）。
   * block: 'nearest' は、すでに見えているときに一覧を動かさないため。
   *
   * Why not ref + マウント時の useEffect: Menu の中身は Modal が後のコミットで描くので、
   * この階層の effect が走る時点では ref が null のまま（実測で確認）。行が付いたことを
   * state で受け直すと、Menu 内部の autoFocus より後に走らせられる。
   */
  useEffect(() => {
    if (!anchorEl || !currentRow) return;
    currentRow.scrollIntoView({ block: 'nearest' });
  }, [anchorEl, currentRow]);

  const handleSelect = useCallback((d: Pick<PlanningsDriverEntity, 'id' | 'name'>) => {
    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: 'ドライバー選択',
      label: d.name ?? '',
    });
    onSelect(d);
    onClose();
  }, [onSelect, onClose]);

  const handleCreate = useCallback((name: string) => {
    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: 'ドライバー名を入力',
      label: name,
    });
    onCreate?.(name);
    onClose();
  }, [onCreate, onClose]);

  const filtered = drivers.filter((d) => {
    if (d.temporary) return false;
    if (d.id !== currentDriverId && excludeDriverIds.has(d.id)) return false;
    if (!query) return true;
    return (d.name ?? '').toLowerCase().includes(query.toLowerCase());
  });

  const trimmedQuery = query.trim();
  const canCreate = onCreate != null
    && trimmedQuery !== ''
    && !drivers.some((d) => (d.name ?? '').trim() === trimmedQuery);

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500 }}
      slotProps={{ paper: { sx: { p: 0, m: 0 } } }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索・新しい名前"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onCompositionStart={() => setComposing(true)}
        onCompositionEnd={() => setComposing(false)}
      />
      <List sx={{ p: 0, m: 0, minWidth: 200 }}>
        {/* 打った名前がマスタに無いときだけ出す。同名が居るなら、そちらを選ばせて
            同じ人が二人並ぶのを防ぐ */}
        {canCreate && (
          <ListItem
            key="driver-create"
            component="div"
            onClick={() => handleCreate(trimmedQuery)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemText
              primary={`「${trimmedQuery}」を登録して割り当て`}
              primaryTypographyProps={{ fontSize: 14, color: 'primary.main' }}
            />
          </ListItem>
        )}
        {currentDriverId != null && currentDriverId !== UNASSIGNED_DRIVER_ID && [
          <ListItem
            key="driver-unassigned"
            component="div"
            onClick={() => handleSelect({ id: UNASSIGNED_DRIVER_ID, name: DRIVER_UNSET_LABEL })}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemText primary="未設定にする" primaryTypographyProps={{ fontSize: 14 }} />
          </ListItem>,
          <Divider key="unassign-divider" />,
        ]}
        {filtered.length === 0 && (
          <ListItem sx={{ fontSize: 12, color: 'text.secondary' }}>
            <ListItemText primary="選択可能なドライバーがありません" primaryTypographyProps={{ fontSize: 12 }} />
          </ListItem>
        )}
        {filtered.map((d) => {
          const isCurrent = d.id === currentDriverId;
          return (
            <ListItem
              key={`driver-${d.id}`}
              component="div"
              ref={isCurrent ? setCurrentRow : undefined}
              // 選択中の行だけ地を変える。印が無いと、名前が途中で切れている行から
              // いま誰が乗っているのか読み取れない
              aria-current={isCurrent ? true : undefined}
              onClick={() => handleSelect(d)}
              sx={{
                cursor: 'pointer',
                ...(isCurrent && { bgcolor: 'hsl(var(--secondary))' }),
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <ListItemText
                primary={d.name}
                primaryTypographyProps={{ fontSize: 14, fontWeight: isCurrent ? 600 : undefined }}
              />
            </ListItem>
          );
        })}
      </List>
    </Menu>
  );
});

export default DriverSelectMenu;
